// mount.tsx — declarative island mounting.
//
// Flask renders a placeholder anywhere on a page:
//   <div data-island="field-access-panel" data-props='{"dropId": 42}'></div>
// This scans for [data-island], looks the name up in the registry, parses
// data-props as the component's props, and mounts it.
//
// Islands are CODE-SPLIT: the registry maps a name to a dynamic import() thunk,
// not a component, so each island is its own chunk that the browser fetches
// only when a page actually contains its placeholder. The thin entry + shared
// chunks load on every page; a page that mounts one small island no longer
// pays for all the others. React.lazy + Suspense drive the on-demand load, and
// each lazy wrapper is memoised per name so repeated scans reuse one chunk.
import { lazy, StrictMode, Suspense, type ComponentType, type LazyExoticComponent } from 'react'
import { createRoot } from 'react-dom/client'
import { AuthProvider } from './auth'
import { IslandErrorBoundary } from './error-boundary'

export type IslandComponent = ComponentType<Record<string, unknown>>
/** A dynamic import that resolves to the island component as a default export. */
export type IslandLoader = () => Promise<{ default: IslandComponent }>
export type IslandRegistry = Record<string, IslandLoader>

const lazyCache = new Map<string, LazyExoticComponent<IslandComponent>>()

function lazyFor(name: string, loader: IslandLoader): LazyExoticComponent<IslandComponent> {
  let comp = lazyCache.get(name)
  if (!comp) {
    comp = lazy(loader)
    lazyCache.set(name, comp)
  }
  return comp
}

function parseProps(node: HTMLElement, name: string): Record<string, unknown> {
  const raw = node.dataset.props
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    console.error(`[islands] "${name}" has invalid data-props JSON; mounting with no props`)
    return {}
  }
}

function mountNode(node: HTMLElement, name: string, loader: IslandLoader): void {
  node.dataset.islandMounted = '1'
  const Component = lazyFor(name, loader)
  createRoot(node).render(
    <StrictMode>
      <IslandErrorBoundary name={name}>
        <AuthProvider>
          <Suspense fallback={null}>
            <Component {...parseProps(node, name)} />
          </Suspense>
        </AuthProvider>
      </IslandErrorBoundary>
    </StrictMode>,
  )
}

// Lazy islands (data-island-lazy) defer chunk download, module evaluation and
// root creation until the placeholder is near the viewport. Islands inside
// inactive tab panes have no layout (display:none), so the observer fires
// only when their pane becomes active — campaigns/detail.html mounts 7
// islands, 6 of them in tabs the user may never open (~200KB of JS).
// Generalises the WhenVisible pattern from the ward dashboard. Do NOT put
// this attribute on modal islands that register window.* openers on mount:
// hidden modals never intersect, so the opener would never appear.
const LAZY_ROOT_MARGIN = '400px'
let lazyObserver: IntersectionObserver | null = null
const lazyPending = new Map<Element, { name: string; loader: IslandLoader }>()

function observeLazy(node: HTMLElement, name: string, loader: IslandLoader): void {
  if (typeof IntersectionObserver === 'undefined') {
    mountNode(node, name, loader)
    return
  }
  if (!lazyObserver) {
    lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const pending = lazyPending.get(entry.target)
          if (!pending) continue
          lazyPending.delete(entry.target)
          lazyObserver?.unobserve(entry.target)
          mountNode(entry.target as HTMLElement, pending.name, pending.loader)
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN },
    )
  }
  lazyPending.set(node, { name, loader })
  lazyObserver.observe(node)
}

export function mountIslands(registry: IslandRegistry): void {
  const nodes = document.querySelectorAll<HTMLElement>('[data-island]')
  nodes.forEach((node) => {
    if (node.dataset.islandMounted === '1') return
    const name = node.dataset.island
    if (!name) return
    const loader = registry[name]
    if (!loader) {
      console.warn(`[islands] no component registered for "${name}"`)
      return
    }
    if (node.dataset.islandLazy !== undefined) {
      if (lazyPending.has(node)) return
      observeLazy(node, name, loader)
      return
    }
    mountNode(node, name, loader)
  })
}
